/**
 * Client-side image resize to WebP before upload.
 * Uses canvas API — no external dependencies.
 */
export async function resizeToWebp(
  file: File,
  maxEdge: number = 1024,
  quality: number = 0.82,
): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Input is not an image file');
  }

  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  let targetWidth = width;
  let targetHeight = height;

  if (Math.max(width, height) > maxEdge) {
    if (width >= height) {
      targetWidth = maxEdge;
      targetHeight = Math.round((height / width) * maxEdge);
    } else {
      targetHeight = maxEdge;
      targetWidth = Math.round((width / height) * maxEdge);
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
  bitmap.close();

  const webpBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to encode WebP'));
      },
      'image/webp',
      quality,
    );
  });

  // If WebP is larger than original (rare), return original
  if (webpBlob.size > file.size) {
    return file;
  }

  return webpBlob;
}

/**
 * Extract the storage path from a Supabase public URL for the profile-photos bucket.
 * Returns undefined if the URL doesn't match our bucket pattern.
 */
export function extractStoragePath(url: string, bucketName: string = 'profile-photos'): string | undefined {
  const escaped = bucketName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = url.match(new RegExp(`/storage/v1/object/(?:public|sign)/${escaped}/(.+?)(?:\\?|$)`));
  return match?.[1];
}

/**
 * The URL a photo column must STORE: the canonical `/object/public/<bucket>/<path>` form, never
 * the expiring `/object/sign/<bucket>/<path>?token=…` that `PhotoUploader` hands back for display.
 *
 * Why it matters (Maciek 19.10, pet photo visible to its owner only): the storage policies that let
 * other viewers read a published profile's photos match `stored_url LIKE '%' || objects.name`
 * (profile-photos, wall-photos, pet-photos). A stored signed URL ends in `?token=…`, so the match
 * fails and only the owner's own-folder policy lets the photo through. Same origin is kept; any
 * other URL (an SSO avatar, another bucket, empty) passes through unchanged.
 */
export function canonicalStorageUrl(url: string, bucketName: string): string {
  const path = extractStoragePath(url, bucketName);
  if (!path) return url;
  const at = url.indexOf('/storage/v1/object/');
  return `${url.slice(0, at)}/storage/v1/object/public/${bucketName}/${path}`;
}
